// ---------------------------------------------------------------------------
// What the Settings page asks, and what it claims (R18a)
//
// The rules behind four faults of the Settings page, kept out of the page so
// they load and test without it (the page pulls in baseQL entities):
//
//   - Deleting a Sales Rep asks once. It asked twice: the same confirmation
//     was written out two times in a row.
//   - Disconnecting an account asks first and says exactly what happens. It
//     deleted at once. The server deletes only our UserAccount rows and never
//     calls Unipile (entrypoints/sdr/tests/account-disconnect-route-db.test.ts);
//     2.1 and 2.0 share one Unipile workspace, so the account stays connected
//     there — the dialog says so rather than let anyone think it was signed out.
//     Since ACC1 (29.10.) a disconnected account sends nothing, so the dialog
//     names the campaigns that use it, read from the server before it asks.
//   - The warm-up cap shows a number only when the server sent one. The page
//     printed `dailyLimit ?? 100` (or `?? 15`), a limit nobody measured.
//   - A list that failed to load says so. It showed its empty state.
//
// The Settings rebuild (R18b–e) keeps these.
// ---------------------------------------------------------------------------

import { html, type TemplateResult } from 'lit';

import { confirmDialog } from '../confirm-dialog.js';

/** How a flow that asks first ended. */
export type AskedOutcome = 'cancelled' | 'done' | { error: string };

// --- Deleting a Sales Rep ---------------------------------------------------

/** How many accounts are assigned to a rep, or null when they could not be loaded. */
export type AssignedAccounts = { email: number; linkedin: number } | null;

const count = (n: number, what: string) => `${n} ${what}${n === 1 ? '' : 's'}`;

/** What happens to a deleted rep's accounts: the server unassigns them and keeps them. */
function whatHappensToTheAccounts(assigned: AssignedAccounts): string {
  if (assigned === null) return 'Its accounts stay connected and are left without a Sales Representative.';
  const total = assigned.email + assigned.linkedin;
  if (total === 0) return 'No accounts are assigned to it.';
  const listed = [
    assigned.email > 0 ? count(assigned.email, 'email account') : '',
    assigned.linkedin > 0 ? count(assigned.linkedin, 'LinkedIn account') : '',
  ]
    .filter(Boolean)
    .join(' and ');
  return total === 1
    ? `Its ${listed} stays connected and is left without a Sales Representative.`
    : `Its ${listed} stay connected and are left without a Sales Representative.`;
}

/**
 * What happens to its leads' replies (REP1, 29.10.): without a rep the server
 * writes no reply draft, so they get none until their campaign has another rep,
 * which then writes them (entrypoints/sdr/src/server/helpers/no-sales-rep.ts).
 */
const WHAT_HAPPENS_TO_THE_REPLIES =
  'Replies to its leads get no drafts until their campaign has another Sales Representative, who then writes them.';

/**
 * Ask once, then delete the rep (DELETE /api/ai-employees/:id). The server
 * deletes the profile and unassigns its accounts; it does not touch them
 * otherwise.
 */
export async function askAndDeleteSalesRep(
  rep: { _id: string; name?: string },
  assigned: AssignedAccounts,
): Promise<AskedOutcome> {
  const confirmed = await confirmDialog(
    `Delete the Sales Representative “${rep.name || 'Untitled'}”? This cannot be undone. ${whatHappensToTheAccounts(assigned)} ${WHAT_HAPPENS_TO_THE_REPLIES}`,
    { title: 'Delete sales representative', confirmLabel: 'Delete' },
  );
  if (!confirmed) return 'cancelled';
  try {
    const res = await fetch(`/api/ai-employees/${encodeURIComponent(rep._id)}`, { method: 'DELETE' });
    const data = await res.json();
    return data.ok ? 'done' : { error: data.error ?? 'Failed to delete' };
  } catch {
    return { error: 'Failed to delete Sales Representative' };
  }
}

// --- Disconnecting an account -----------------------------------------------

/** What the X on an account row does. */
export const DISCONNECT_ACCOUNT_HINT = 'Disconnect from SDR Agent. The account stays connected in Unipile.';

export interface AccountToDisconnect {
  /** `instantly_email`, `unipile_email` or `unipile_linkedin`: the route's `:provider`. */
  provider: string;
  /** The provider's account id: the route's `:accountId`. */
  accountId: string;
  channel: 'email' | 'linkedin';
  /** What the row shows: the mailbox address or the LinkedIn name. */
  label: string;
  /** The Sales Rep whose row it sits in. */
  repName?: string;
  /** The mailbox carries the "Warmed up" mark, which is stored on the row that goes. */
  warmedUp?: boolean;
}

/** A campaign that would send through the account, as the server lists it. */
interface CampaignUsingIt {
  name?: string;
}

/** How many campaign names the dialog spells out. */
const NAMED_AT_MOST = 5;

const quoted = (names: string[]) => names.map(n => `“${n || 'Untitled'}”`);

/** "“A”", "“A” and “B”", "“A”, “B” and “C”". */
function listed(names: string[]): string {
  const q = quoted(names);
  return q.length <= 1 ? (q[0] ?? '') : `${q.slice(0, -1).join(', ')} and ${q[q.length - 1]}`;
}

const STOP = 'stop until another account is assigned or this account is connected again.';

/**
 * What happens to the campaigns that use it. A disconnected account sends
 * nothing (ACC1, entrypoints/sdr/tests/disconnected-account-sends-nothing-db.test.ts):
 * their steps through it wait, with a reason on the campaign page. Null: the
 * list could not be read, which changes nothing about what happens.
 */
function whatHappensToTheCampaigns(campaigns: CampaignUsingIt[] | null): string {
  if (campaigns === null)
    return ` The campaigns that use it could not be loaded; their steps through this account ${STOP}`;
  if (campaigns.length === 0) return ' No campaign uses it.';
  const names = campaigns.map(c => c.name ?? '');
  if (names.length === 1) return ` The campaign ${listed(names)} uses it: its steps through this account ${STOP}`;
  if (names.length <= NAMED_AT_MOST) {
    return ` ${names.length} campaigns use it: ${listed(names)}. Their steps through this account ${STOP}`;
  }
  return ` ${names.length} campaigns use it, among them ${listed(names.slice(0, NAMED_AT_MOST))}. Their steps through this account ${STOP}`;
}

function disconnectMessage(account: AccountToDisconnect, campaigns: CampaignUsingIt[] | null): string {
  const rep = account.repName ? `the Sales Representative “${account.repName}”` : 'its Sales Representative';
  const warm = account.warmedUp ? ' Its “Warmed up” mark is removed with it.' : '';
  return (
    `Disconnect “${account.label}” from SDR Agent? It is removed from Settings and from ${rep}.${warm}` +
    ' SDR Agent does not sign it out of Unipile: it stays connected there.' +
    whatHappensToTheCampaigns(campaigns)
  );
}

/** How long the dialog waits for the campaign list before it asks without it. */
const CAMPAIGNS_WAIT_MS = 5000;

/**
 * The campaigns that use the account (GET /api/accounts/:provider/:accountId/campaigns),
 * or null when they could not be read in time. A read: it changes nothing.
 */
async function campaignsUsing(account: AccountToDisconnect): Promise<CampaignUsingIt[] | null> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const late = new Promise<null>(resolve => {
    timer = setTimeout(() => resolve(null), CAMPAIGNS_WAIT_MS);
  });
  const read = (async () => {
    try {
      const res = await fetch(`/api/accounts/${account.provider}/${encodeURIComponent(account.accountId)}/campaigns`);
      const data = (await res.json()) as { ok?: boolean; campaigns?: unknown };
      return data.ok && Array.isArray(data.campaigns) ? (data.campaigns as CampaignUsingIt[]) : null;
    } catch {
      return null;
    }
  })();
  try {
    return await Promise.race([read, late]);
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Ask first, then disconnect (DELETE /api/accounts/:provider/:accountId). The
 * server deletes this user's UserAccount row for that account and makes no
 * Unipile request. Before asking, it reads which campaigns use the account;
 * nothing else leaves before the yes.
 */
export async function askAndDisconnectAccount(account: AccountToDisconnect): Promise<AskedOutcome> {
  const message = disconnectMessage(account, await campaignsUsing(account));
  const confirmed =
    account.channel === 'email'
      ? await confirmDialog(message, { title: 'Disconnect email account', confirmLabel: 'Disconnect' })
      : await confirmDialog(message, { title: 'Disconnect LinkedIn account', confirmLabel: 'Disconnect' });
  if (!confirmed) return 'cancelled';
  try {
    const res = await fetch(`/api/accounts/${account.provider}/${encodeURIComponent(account.accountId)}`, {
      method: 'DELETE',
    });
    const data = await res.json();
    return data.ok ? 'done' : { error: data.error ?? 'Failed to disconnect' };
  } catch {
    return { error: 'Failed to disconnect account' };
  }
}

// --- The warm-up cap --------------------------------------------------------

const measured = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);

/** The line under "Warmed up": the daily cap the server sent, or none. */
export function warmupCapLabel(warmed: boolean, dailyLimit: unknown): string {
  if (warmed) return measured(dailyLimit) ? `${dailyLimit}/day` : 'full limit';
  return measured(dailyLimit) ? `ramp · ${dailyLimit}/day` : 'on the ramp';
}

/** The toast after the warm-up switch, from the server's answer. */
export function warmupToast(warmed: boolean, answer: { dailyLimit?: unknown; releasedJobs?: unknown }): string {
  if (!warmed) return 'Warm-up removed — the mailbox is back on the ramp.';
  const released = measured(answer.releasedJobs) && answer.releasedJobs > 0 ? answer.releasedJobs : 0;
  if (!measured(answer.dailyLimit)) {
    return released
      ? `Mailbox marked as warmed up — ${released} queued email(s) released.`
      : 'Mailbox marked as warmed up.';
  }
  return (
    `Mailbox marked as warmed up — daily limit ${answer.dailyLimit}` +
    (released ? `, ${released} queued email(s) released.` : '.')
  );
}

// --- Load failures ----------------------------------------------------------

/** Which of the page's lists did not load. */
export interface SettingsLoadFailures {
  reps: boolean;
  email: boolean;
  linkedin: boolean;
  crmConfig: boolean;
  crmConnections: boolean;
}

export const NOTHING_FAILED: SettingsLoadFailures = {
  reps: false,
  email: false,
  linkedin: false,
  crmConfig: false,
  crmConnections: false,
};

/** A REST answer as the page keeps it; a request that threw is kept as `{ ok: false }`. */
type Answer = { ok?: boolean } | null | undefined;

/**
 * Which lists failed, from what each source answered. A list that answered
 * with nothing in it did not fail.
 *
 * The Instantly listing (GET /api/accounts/email) is left out on purpose: 2.1
 * sets no INSTANTLY_API_KEY (docs/migration/RUNBOOK.md), so that route always
 * answers 500 "not configured", and counting it would show a failure on every
 * visit. The mailboxes that send are Unipile's.
 */
export function settingsLoadFailures(answers: {
  reps: Answer;
  unipileEmail: Answer;
  linkedin: Answer;
  /** The CRM config, or null when the call failed. */
  crmConfig: unknown;
  /** The CRM connections, or null when the call failed. */
  crmConnections: unknown;
}): SettingsLoadFailures {
  return {
    reps: !answers.reps?.ok,
    email: !answers.unipileEmail?.ok,
    linkedin: !answers.linkedin?.ok,
    crmConfig: answers.crmConfig == null,
    crmConnections: !Array.isArray(answers.crmConnections),
  };
}

/** Where a list that failed to load would have shown its empty state. */
export function loadFailedNotice(what: string, retry: () => void): TemplateResult {
  return html`<div
    class="border-base-300 text-error flex items-center justify-center gap-2 rounded-lg border border-dashed p-3 text-xs"
    role="alert"
  >
    <span>${what} could not be loaded.</span>
    <button type="button" class="btn btn-ghost btn-xs" @click=${retry}>Try again</button>
  </div>`;
}
